import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import TestRunner from './components/TestRunner';
import NeuralProfile, { ArchetypeIcon } from './components/NeuralProfile';
import ScoutDashboard from './components/ScoutDashboard';
import PracticeRunner from './components/PracticeRunner';
import AuthModal from './components/AuthModal';
import AuthCallback from './components/AuthCallback';
import Shortlist from './components/Shortlist';
import CompareView from './components/CompareView';
import Leaderboard from './components/Leaderboard';
import CombineComplete from './components/CombineComplete';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import MyProfile from './components/MyProfile';
import Pricing from './components/Pricing';
import BillingPage from './components/BillingPage';
import ResetPassword from './components/ResetPassword';
import SensitivityConverter from './components/SensitivityConverter';
import EmbedLeaderboard from './components/EmbedLeaderboard';
import AdminDashboard from './components/AdminDashboard';
import ChallengeView from './components/ChallengeView';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import ForCoaches from './components/ForCoaches';
import Science from './components/Science';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { startSession, getStats, getLeaderboard, getRecentProfiles } from './services/api';
import { initPixels } from './pixels';

const GENRE_MODULE_KEYS = {
  general: ['reaction', 'aim', 'tracking', 'memory', 'composure', 'tilt'],
  fps:     ['reaction', 'aim', 'tracking', 'flicker', 'composure', 'tilt'],
  moba:    ['reaction', 'sequencing', 'tracking', 'memory', 'composure', 'tilt'],
};

const GENRES = [
  {
    id: 'general', label: 'General Combine', shortLabel: 'General',
    desc: 'Full cognitive battery — all roles',
    svg: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    id: 'fps', label: 'FPS Combine', shortLabel: 'FPS',
    desc: 'Optimized for shooter players',
    svg: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3" />
        <line x1="12" y1="3" x2="12" y2="7" />
        <line x1="12" y1="17" x2="12" y2="21" />
        <line x1="3" y1="12" x2="7" y2="12" />
        <line x1="17" y1="12" x2="21" y2="12" />
      </svg>
    ),
  },
  {
    id: 'moba', label: 'MOBA Combine', shortLabel: 'MOBA',
    desc: 'Optimized for strategy & support',
    svg: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
      </svg>
    ),
  },
];

const MODULES = [
  {
    num: '01', label: 'Reaction Speed', tag: 'All Genres', dimension: 'Raw Speed',
    color: '#00d4ff',
    desc: 'Simple RT, choice RT, and go/no-go tasks measure raw neural processing speed and impulse control.',
    svg: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
  },
  {
    num: '02', label: 'Aim Precision', tag: 'General & FPS', dimension: 'Aim Precision',
    color: '#ef4444',
    desc: 'Randomized targets measure click accuracy, movement speed, and center-shot consistency under pressure.',
    svg: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3" />
        <line x1="12" y1="2" x2="12" y2="6" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="2" y1="12" x2="6" y2="12" />
        <line x1="18" y1="12" x2="22" y2="12" />
      </svg>
    ),
  },
  {
    num: '03', label: 'Target Tracking', tag: 'All Genres', dimension: 'Tracking Accuracy',
    color: '#10b981',
    desc: 'A Lissajous-curve target tests sustained cursor control on smooth but unpredictable paths.',
    svg: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M21 12a9 9 0 1 1-6.22-8.56" strokeDasharray="3 2.2" />
        <circle cx="18" cy="6" r="2.2" fill="currentColor" fillOpacity="0.2" />
        <path d="M12 12 L18 6" strokeWidth="1.2" opacity="0.5" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    num: '04', label: 'Working Memory', tag: 'General & MOBA', dimension: 'Working Memory',
    color: '#a855f7',
    desc: 'Spatial grid recall — watch a sequence of cells flash, then reproduce the exact order. Sequence length increases each round.',
    svg: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" opacity="0.45" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" opacity="0.45" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
        <path d="M10 6.5h4M17.5 10v4M14 17.5h-4M6.5 14v-4" strokeWidth="1.1" opacity="0.5" strokeDasharray="1.5 1" />
      </svg>
    ),
  },
  {
    num: '05', label: 'Target Sequencing', tag: 'MOBA', dimension: 'Prioritization',
    color: '#f59e0b',
    desc: 'Multi-target ordering under time pressure tests spatial prioritization and rapid execution speed.',
    svg: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="5" cy="5" r="2.5" />
        <circle cx="19" cy="5" r="2.5" />
        <circle cx="19" cy="19" r="2.5" />
        <circle cx="5" cy="19" r="2.5" />
        <path d="M7.5 5h9M19 7.5v9M16.5 19h-9M5 16.5v-9" strokeWidth="1.2" strokeDasharray="2 1.5" opacity="0.5" />
      </svg>
    ),
  },
  {
    num: '06', label: 'Flick Speed', tag: 'FPS', dimension: 'Flick Speed',
    color: '#ff6b35',
    desc: 'Brief disappearing targets test elite-level flick reactions — click before the target vanishes.',
    svg: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="12" cy="12" r="2.5" fill="currentColor" fillOpacity="0.2" />
        <path d="M12 3v3.5M12 17.5v3.5M3 12h3.5M17.5 12H21" />
        <path d="M6.4 6.4l2.3 2.3M15.3 15.3l2.3 2.3M6.4 17.6l2.3-2.3M15.3 8.7l2.3-2.3" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    num: '07', label: 'Composure Under Fire', tag: 'All Genres', dimension: 'Decision Quality',
    color: '#06b6d4',
    desc: 'Flanker task — identify the center arrow while flankers and sudden distractions compete for your attention.',
    svg: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12h4M10 12h4M20 12h-2" />
        <path d="M14 9l3 3-3 3" />
        <circle cx="12" cy="12" r="9" strokeOpacity="0.3" />
        <path d="M7 9l-3 3 3 3" strokeOpacity="0.4" />
        <path d="M17 9l3 3-3 3" strokeOpacity="0.4" />
      </svg>
    ),
  },
  {
    num: '08', label: 'Tilt Recovery', tag: 'All Genres', dimension: 'Consistency',
    color: '#f43f5e',
    desc: 'Place a risk bet before each round, then respond to a go/no-go stimulus. Failure phase induces tilt — your bet calibration and recovery accuracy reveal composure under pressure.',
    svg: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 17l4-8 4 5 3-3 4 6" />
        <circle cx="19" cy="6" r="2" fill="currentColor" fillOpacity="0.2" />
        <path d="M19 4v4M17 6h4" strokeWidth="1.2" />
      </svg>
    ),
  },
];

const MODULE_DATA_BY_KEY = {
  reaction:   MODULES[0],
  aim:        MODULES[1],
  tracking:   MODULES[2],
  memory:     MODULES[3],
  sequencing: MODULES[4],
  flicker:    MODULES[5],
  composure:  MODULES[6],
  tilt:       MODULES[7],
};

const ARCHETYPE_SHOWCASE = [
  { archetypeId: 'ANCHOR',     name: 'The Anchor',     role: 'General Combine', color: '#00d4ff', desc: 'Exceptional reaction speed and rock-solid consistency. The bedrock of any competitive team.' },
  { archetypeId: 'CLUTCH',     name: 'The Clutch',     role: 'General Combine', color: '#ff6b35', desc: 'Performs best under pressure. High decision quality exactly when the stakes are highest.' },
  { archetypeId: 'FRAGGER',    name: 'The Fragger',    role: 'FPS Combine',     color: '#ef4444', desc: 'Dominant aim precision and flick speed. Built for entry frags and pure aggression.' },
  { archetypeId: 'STRATEGIST', name: 'The Strategist', role: 'MOBA Combine',    color: '#a855f7', desc: 'Elite working memory and target prioritization. Sees the board that others miss entirely.' },
  { archetypeId: 'AWPER',      name: 'The AWPer',      role: 'FPS Combine',     color: '#10b981', desc: 'Perfect stillness before the shot. Tracking accuracy and flick precision in rare harmony.' },
  { archetypeId: 'SUPPORT',    name: 'The Support',    role: 'MOBA Combine',    color: '#f59e0b', desc: 'High decision quality and spatial awareness. Low-ego, high-impact cognitive profile.' },
];

const FAQ_ITEMS = [
  {
    q: 'What is NeuroRank?',
    a: 'NeuroRank is an esports cognitive combine — a structured assessment of the mental skills that determine in-game performance. You complete 6 timed modules (reaction, aim, tracking, memory, composure, tilt) and receive a scored neural profile with an archetype classification.',
  },
  {
    q: 'How is this different from just playing ranked?',
    a: 'Rank reflects mechanical practice and game knowledge, not raw cognitive ability. Two players can share the same rank but have completely different cognitive profiles — one is fast but tilts, another is calm but slow. NeuroRank isolates the underlying dimensions so you can see exactly where you stand.',
  },
  {
    q: 'How long does the combine take?',
    a: 'About 10–15 minutes. There are 6 modules, each lasting 60–90 seconds. You get a shareable result at the end with your full neural profile.',
  },
  {
    q: 'Is the combine free?',
    a: 'Yes — the full cognitive combine, your shareable neural profile, and leaderboard access are completely free. Paid plans unlock scout dashboard features for talent discovery.',
  },
  {
    q: 'Can I retake the combine?',
    a: 'Yes, with a 24-hour cooldown to prevent grinding for a better score. Your most recent result is shown on your profile. You can skip the cooldown with a Re-test Bypass Credit ($1.99).',
  },
  {
    q: 'What genres are supported?',
    a: 'General (default), FPS (replaces memory with flicker detection), and MOBA (replaces aim with sequencing). Each genre variant tests the cognitive skills most relevant to that game type.',
  },
];

function FAQ() {
  const [open, setOpen] = React.useState(null);
  return (
    <section className="home-section">
      <div className="container" style={{ maxWidth: 720 }}>
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="section-eyebrow">FAQ</div>
          <h2 className="section-title">Common Questions</h2>
        </motion.div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {FAQ_ITEMS.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                style={{
                  width: '100%', textAlign: 'left', background: 'none', border: 'none',
                  borderBottom: '1px solid var(--border)', padding: '18px 0',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{item.q}</span>
                <span style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0, lineHeight: 1, transform: open === i ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }}>+</span>
              </button>
              {open === i && (
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75, padding: '12px 0 20px' }}>
                  {item.a}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TICKER_ITEMS = [
  'Reaction Speed', 'Aim Precision', 'Target Tracking', 'Working Memory',
  'Target Sequencing', 'Flick Speed', 'Decision Quality', 'Neural Profile',
  'Player Archetype', 'Consistency Score', 'Cognitive Combine', 'NeuroRank',
];

function TickerStrip() {
  const doubled = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="ticker-strip">
      <div className="ticker-track">
        {doubled.map((item, i) => (
          <span key={i} className="ticker-item">
            <span className="ticker-item__dot" />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function ArchetypeShowcase() {
  return (
    <section className="home-section">
      <div className="container--wide">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="section-title">Know Your Archetype</h2>
          <p className="section-sub">18 player profiles across three combine types. Which one are you?</p>
        </motion.div>
        <div className="archetype-grid">
          {ARCHETYPE_SHOWCASE.map(({ archetypeId, name, role, color, desc }, i) => (
            <motion.div
              key={name}
              className="archetype-card"
              style={{ '--archetype-color': color }}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.07 }}
            >
              <span className="archetype-card__icon"><ArchetypeIcon archetypeId={archetypeId} size={32} color={color} /></span>
              <div className="archetype-card__name">{name}</div>
              <div className="archetype-card__role">{role}</div>
              <p className="archetype-card__desc">{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Landing Page ---
function Landing() {
  const navigate = useNavigate();
  const { user, authLoading, logout } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const showUserLinks = !!user || (authLoading && !!localStorage.getItem('nr_token'));
  const unreadCount = user?.notifications?.filter((n) => !n.read).length || 0;
  const [playerTag, setPlayerTag] = useState('');
  const [genre, setGenre] = useState('general');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [profileCount, setProfileCount] = useState(null);
  const [featuredProfiles, setFeaturedProfiles] = useState([]);
  const [recentProfiles, setRecentProfiles] = useState([]);

  React.useEffect(() => { initPixels(); }, []);

  React.useEffect(() => {
    getStats().then(({ profileCount: c }) => setProfileCount(c)).catch(() => {});
    getLeaderboard({ genre: 'all', sortBy: 'overallScore', limit: 3 })
      .then(({ entries }) => setFeaturedProfiles(entries))
      .catch(() => {});
    getRecentProfiles(8).then(setRecentProfiles).catch(() => {});
  }, []);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      localStorage.setItem('nr_ref', refCode);
    }
    const utm = {
      utm_source:   urlParams.get('utm_source'),
      utm_medium:   urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_content:  urlParams.get('utm_content'),
    };
    if (utm.utm_source) {
      sessionStorage.setItem('nr_utm', JSON.stringify(utm));
    }
    if (refCode || utm.utm_source) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleConfirmDirect = async (e) => {
    e.preventDefault();
    if (!playerTag.trim() || playerTag.trim().length < 2) {
      setError('Enter a player tag (at least 2 characters).');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { sessionId } = await startSession(playerTag.trim(), genre);
      sessionStorage.setItem('nr_genre', genre);
      try {
        const utm = JSON.parse(sessionStorage.getItem('nr_utm') || '{}');
        window.posthog?.capture('combine_started', { genre, ...utm });
      } catch {}
      navigate(`/test?session=${sessionId}`);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 429 && data?.cooldownMs) {
        const hours = Math.ceil(data.cooldownMs / 3600000);
        setError(`Cooldown active — you can retake in ~${hours}h. Want to skip? Get a Re-test Bypass Credit ($1.99) on the pricing page.`);
      } else {
        setError(data?.error || 'Failed to start session. Try again.');
      }
      setLoading(false);
    }
  };

  const handleStart = (e) => {
    e.preventDefault();
    if (!playerTag.trim() || playerTag.trim().length < 2) {
      setError('Enter a player tag (at least 2 characters).');
      return;
    }
    setError('');
    setShowInterstitial(true);
    import('./services/track').then(({ track }) => track('landing_cta_click', { genre }));
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    import('./services/track').then(({ track }) => track('combine_genre_select', { genre }));
    try {
      const { sessionId } = await startSession(playerTag.trim(), genre);
      sessionStorage.setItem('nr_genre', genre);
      import('./services/track').then(({ track }) => track('combine_start', { genre, sessionId }));
      try {
        const utm = JSON.parse(sessionStorage.getItem('nr_utm') || '{}');
        window.posthog?.capture('combine_started', { genre, ...utm });
      } catch {}
      navigate(`/test?session=${sessionId}`);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 429 && data?.cooldownMs) {
        const hours = Math.ceil(data.cooldownMs / 3600000);
        setError(`Cooldown active — you can retake in ~${hours}h. Want to skip? Get a Re-test Bypass Credit ($1.99) on the pricing page.`);
      } else {
        setError(data?.error || 'Failed to start session. Try again.');
      }
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="home-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* ── Sticky Nav ───────────────────────────────────────────────── */}
      <nav className="home-nav">
        <span className="home-nav__logo">NEURORANK</span>
        <div className="home-nav__links">
          <Link to="/practice" className="home-nav__link">Practice</Link>
          <Link to="/leaderboard" className="home-nav__link">Leaderboard</Link>
          <Link to="/scout" className="home-nav__link">Scout Dashboard</Link>
          <Link to="/blog" className="home-nav__link">Blog</Link>
          <Link to="/sensitivity" className="home-nav__link">Sens Converter</Link>
          <Link to="/pricing" className="home-nav__link">Pricing</Link>
          {showUserLinks ? (
            <>
              <Link to="/my-profile" className="home-nav__link" style={{ color: 'var(--accent)', fontWeight: 700, position: 'relative' }}>
                {user?.playerTag || 'My Profile'}
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -12,
                    background: '#ef4444', color: '#fff',
                    fontSize: 10, fontWeight: 700,
                    width: 16, height: 16, borderRadius: '50%',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
              {user && <button onClick={logout} className="btn btn--ghost home-nav__auth-btn">Sign Out</button>}
            </>
          ) : (
            <button onClick={() => setShowAuth(true)} className="btn btn--primary home-nav__auth-btn">Sign In</button>
          )}
        </div>
        {/* Mobile hamburger */}
        <button
          className="app-nav__hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: 'var(--text-primary)' }}
        >
          {menuOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="17" x2="21" y2="17"/></svg>
          )}
        </button>
      </nav>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div style={{
          position: 'fixed', top: 60, left: 0, right: 0, zIndex: 99,
          background: 'rgba(9,14,26,0.97)', backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid var(--border)',
          padding: '8px 0 16px',
        }}>
          {[
            { to: '/practice', label: 'Practice' },
            { to: '/leaderboard', label: 'Leaderboard' },
            { to: '/scout', label: 'Scout Dashboard' },
            { to: '/blog', label: 'Blog' },
            { to: '/sensitivity', label: 'Sens Converter' },
            { to: '/pricing', label: 'Pricing' },
            ...(showUserLinks ? [{ to: '/my-profile', label: 'My Profile' }] : []),
          ].map(({ to, label }) => (
            <Link key={to} to={to} onClick={() => setMenuOpen(false)} style={{
              display: 'block', padding: '13px 24px',
              fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: 'var(--text-secondary)', textDecoration: 'none',
              borderBottom: '1px solid var(--border)',
            }}>{label}</Link>
          ))}
          <div style={{ padding: '12px 24px 0' }}>
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 700 }}>{user.playerTag}</span>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="btn btn--ghost" style={{ padding: '6px 14px', fontSize: 11 }}>Sign Out</button>
              </div>
            ) : (
              <button onClick={() => { setShowAuth(true); setMenuOpen(false); }} className="btn btn--primary" style={{ width: '100%', padding: '11px' }}>Sign In</button>
            )}
          </div>
        </div>
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}

      {/* ── Pre-test Interstitial ─────────────────────────────────────── */}
      {showInterstitial && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(6,9,15,0.97)', backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          overflowY: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '40px 16px 60px',
        }}>
          <div style={{ width: '100%', maxWidth: 680 }}>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div style={{ fontSize: 11, letterSpacing: '0.12em', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10 }}>
                {GENRES.find(g => g.id === genre)?.label || 'Combine'}
              </div>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)', margin: 0, lineHeight: 1.2 }}>
                Ready, <span style={{ color: '#00d4ff' }}>{playerTag}</span>?
              </h2>
              <p style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.6 }}>
                6 modules · ~10 minutes · results shown immediately after
              </p>
            </div>

            {/* Module list */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>
                What you&apos;ll complete
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {GENRE_MODULE_KEYS[genre].map((key, i) => {
                  const mod = MODULE_DATA_BY_KEY[key];
                  if (!mod) return null;
                  return (
                    <div key={key} style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)',
                      borderRadius: 10, padding: '12px 16px',
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        background: `${mod.color}18`, border: `1px solid ${mod.color}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: mod.color,
                      }}>
                        {mod.svg}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                          <span style={{ color: 'var(--text-muted)', fontWeight: 500, marginRight: 6 }}>0{i + 1}</span>
                          {mod.label}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {mod.desc}
                        </div>
                      </div>
                      <div style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                        color: mod.color, background: `${mod.color}18`, border: `1px solid ${mod.color}30`,
                        borderRadius: 6, padding: '3px 8px', flexShrink: 0,
                      }}>
                        {mod.dimension}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* You'll receive */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>
                You&apos;ll receive
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { label: 'Neural Profile', icon: '◈', color: '#00d4ff' },
                  { label: 'Archetype Classification', icon: '⬡', color: '#a855f7' },
                  { label: 'AI Scout Report', icon: '◎', color: '#10b981' },
                ].map(({ label, icon, color }) => (
                  <div key={label} style={{
                    flex: '1 1 180px', display: 'flex', alignItems: 'center', gap: 10,
                    background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: '12px 16px',
                  }}>
                    <span style={{ fontSize: 18, color }}>{icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div style={{
              background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)',
              borderRadius: 10, padding: '12px 16px', marginBottom: 32,
              display: 'flex', flexWrap: 'wrap', gap: '6px 24px',
            }}>
              {[
                { icon: '⊙', text: 'Mouse required — not trackpad' },
                { icon: '⊞', text: 'Keep this tab active' },
                { icon: '◌', text: 'Quiet space, no distractions' },
              ].map(({ icon, text }) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                  <span style={{ color: '#00d4ff', fontSize: 14 }}>{icon}</span>
                  {text}
                </div>
              ))}
            </div>

            {/* Actions */}
            {error && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 16, textAlign: 'center' }}>{error}</p>}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                onClick={() => setShowInterstitial(false)}
                style={{
                  background: 'none', border: '1px solid var(--border)', borderRadius: 10,
                  color: 'var(--text-muted)', fontSize: 13, fontWeight: 600,
                  padding: '14px 24px', cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                ← Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                style={{
                  flex: 1, background: '#00d4ff', color: '#000', borderRadius: 10,
                  fontWeight: 800, fontSize: 15, letterSpacing: '0.04em',
                  padding: '14px 24px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1, transition: 'all 0.15s',
                }}
              >
                {loading ? 'Starting…' : 'Begin Combine →'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero__orb hero__orb--1" />
        <div className="hero__orb hero__orb--2" />
        <div className="hero__orb hero__orb--3" />
        <div className="hero__grid" />
        <div className="hero__scan" />

        <motion.div
          className="hero__content"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: 'easeOut' }}
        >
          <div className="hero__eyebrow">THE ESPORTS COGNITIVE COMBINE</div>

          <h1 className="hero__headline" aria-label="Discover the brain behind the rank.">
            <span style={{ display: 'block', overflow: 'hidden' }}>
              <motion.span
                style={{ display: 'block' }}
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.72, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
              >
                Discover the brain
              </motion.span>
            </span>
            <span style={{ display: 'block', overflow: 'hidden' }}>
              <motion.span
                className="hero__headline--accent"
                style={{ display: 'block' }}
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.72, ease: [0.16, 1, 0.3, 1], delay: 0.28 }}
              >
                behind the rank.
              </motion.span>
            </span>
          </h1>

          <p className="hero__sub">
            A 10-minute cognitive test battery that measures what rank can't —
            reaction speed, aim precision, target tracking, and working memory.
            Get your shareable neural profile.
          </p>

          {/* Start Form */}
          {(
            <form onSubmit={handleStart} className="hero__form">
              <div className="hero__form-card">

                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                  ~10 minutes · 6 modules · free{profileCount !== null && profileCount >= 50 && (
                    <span style={{ color: '#10b981' }}> · {profileCount.toLocaleString()} players tested</span>
                  )}
                </p>

                {/* Tag input */}
                <div className="hero__field">
                  <label className="hero__field-label">Player Tag</label>
                  <input
                    className="input hero__tag-input"
                    type="text"
                    placeholder="e.g. NightOwl99"
                    value={playerTag}
                    onChange={(e) => { setPlayerTag(e.target.value); setError(''); }}
                    maxLength={32}
                    disabled={loading}
                    autoComplete="off"
                  />
                </div>

                {/* Genre — compact toggle, General pre-selected */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.05em', marginBottom: 8, display: 'block' }}>Combine Type</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {GENRES.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setGenre(g.id)}
                        disabled={loading}
                        style={{
                          flex: 1, padding: '8px 6px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                          background: genre === g.id ? 'rgba(0,212,255,0.12)' : 'transparent',
                          border: `1px solid ${genre === g.id ? 'rgba(0,212,255,0.4)' : 'var(--border)'}`,
                          color: genre === g.id ? '#00d4ff' : 'var(--text-muted)',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        {g.shortLabel}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 18, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  Use a mouse (not trackpad) · keep this tab focused · find a quiet spot
                </div>

                {/* CTA */}
                <button className="btn btn--primary hero__cta-btn" type="submit" disabled={loading}>
                  {loading ? 'Starting…' : 'Start the Combine →'}
                </button>

                {error && <p className="hero__error">{error}{error.includes('Bypass Credit') && (
                  <>{' '}<Link to="/pricing#boosts" style={{ color: '#10b981', textDecoration: 'underline' }}>Go to Pricing →</Link></>
                )}</p>}

                <p className="hero__tos">
                  By starting, you agree to our{' '}
                  <Link to="/terms">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy">Privacy Policy</Link>.
                  Your profile will be publicly visible.
                </p>
              </div>
            </form>
          )}


        </motion.div>

        {/* Scroll hint */}
        <motion.div
          className="hero__scroll"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
        >
          <span className="hero__scroll-label">scroll</span>
          <div className="hero__scroll-track">
            <div className="hero__scroll-dot" />
          </div>
          <svg className="hero__scroll-chevron" width="14" height="8" viewBox="0 0 14 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="1,1 7,7 13,1" />
          </svg>
        </motion.div>
      </section>

      {/* ── Ticker Strip ─────────────────────────────────────────────── */}
      <TickerStrip />

      {/* ── Stats Strip ──────────────────────────────────────────────── */}
      <section className="stats-strip">
        <div className="container--wide stats-strip__inner">
          {[
            { value: '8',   label: 'Test Modules' },
            { value: '10',  label: 'Minutes to Complete' },
            { value: '6',   label: 'Dimensions Scored' },
            { value: '18',  label: 'Player Archetypes' },
            ...(profileCount !== null && profileCount >= 50 ? [{ value: profileCount.toLocaleString(), label: 'Players Tested', live: true }] : []),
          ].map(({ value, label, live }) => (
            <div key={label} className="stat-item">
              <span className="stat-value" style={live ? { color: '#10b981' } : {}}>{value}</span>
              <span className="stat-label">{label}{live && <span style={{ color: '#10b981', marginLeft: 4, fontSize: 9 }}>●</span>}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Recent Activity ──────────────────────────────────────────── */}
      {recentProfiles.length > 0 && (
        <div style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,212,255,0.02)', overflowX: 'auto', scrollbarWidth: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 'max-content', padding: '0 24px', maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', paddingRight: 24, flexShrink: 0 }}>
              <span style={{ fontSize: 9, letterSpacing: 2, color: '#10b981', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>● LIVE</span>
            </div>
            {recentProfiles.map((p) => (
              <a
                key={p.shareableId}
                href={`/profile/${p.shareableId}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '11px 20px', borderLeft: '1px solid var(--border)',
                  textDecoration: 'none', flexShrink: 0,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{p.playerTag}</span>
                {p.archetype && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {p.archetype}</span>}
                {p.overallScore != null && (
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>{p.overallScore}</span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* ── How It Works ─────────────────────────────────────────────── */}
      <section className="home-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="section-title">How It Works</h2>
            <p className="section-sub">Three steps to your neural profile.</p>
          </motion.div>

          <div className="steps-grid">
            {[
              {
                step: '// 01',
                title: 'Choose Your Combine',
                desc: 'Select General, FPS, or MOBA mode. Each unlocks a tailored set of cognitive modules built for your game type.',
              },
              {
                step: '// 02',
                title: 'Complete the Tests',
                desc: 'Back-to-back modules test your reaction, aim, tracking, memory, composure, and more — all in real competitive conditions.',
              },
              {
                step: '// 03',
                title: 'Get Your Profile',
                desc: 'Receive a shareable neural profile with scores across 6 dimensions, achievement badges, and your personal player archetype.',
              },
            ].map(({ step, title, desc }, i) => (
              <motion.div
                key={step}
                className="step-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.12 }}
              >
                <div className="step-card__number">{step}</div>
                <h3 className="step-card__title">{title}</h3>
                <p className="step-card__desc">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Archetype Showcase ───────────────────────────────────────── */}
      <ArchetypeShowcase />

      {/* ── Module Showcase ───────────────────────────────────────────── */}
      <section className="home-section home-section--alt">
        <div className="container--wide">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="section-title">What We Measure</h2>
            <p className="section-sub">Each module isolates a distinct cognitive dimension under pressure.</p>
          </motion.div>

          <div className="modules-grid">
            {MODULES.map(({ num, svg, label, tag, desc, dimension, color }, i) => (
              <motion.div
                key={label}
                className="module-card"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
              >
                <div className="module-card__top">
                  <div className="module-card__icon-wrap" style={{ color }}>{svg}</div>
                  <div className="module-card__meta">
                    <span className="module-card__num">{num}</span>
                    <span className="module-card__tag">{tag}</span>
                  </div>
                </div>
                <h3 className="module-card__title">{label}</h3>
                <p className="module-card__desc">{desc}</p>
                <div className="module-card__dimension">
                  <span>Measures</span>
                  <span>{dimension}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Profiles ────────────────────────────────────────── */}
      {featuredProfiles.length > 0 && (
        <section className="home-section home-section--alt">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="section-title">Top Profiles</h2>
              <p className="section-sub">The highest-scoring neural profiles on the platform.</p>
            </motion.div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 680, margin: '0 auto' }}>
              {featuredProfiles.map((entry, i) => {
                const rankColor = { 1: '#f59e0b', 2: '#94a3b8', 3: '#cd7f32' }[entry.rank];
                const medal = ['🥇', '🥈', '🥉'][i];
                const genreColor = { general: '#00d4ff', fps: '#ef4444', moba: '#a855f7' }[entry.genre] || '#00d4ff';
                return (
                  <motion.a
                    key={entry.shareableId}
                    href={`/profile/${entry.shareableId}`}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.1 }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 20,
                      padding: '18px 24px', borderRadius: 12,
                      border: `1px solid ${rankColor}33`,
                      background: `${rankColor}08`,
                      textDecoration: 'none',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = `${rankColor}14`; e.currentTarget.style.borderColor = `${rankColor}66`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = `${rankColor}08`; e.currentTarget.style.borderColor = `${rankColor}33`; }}
                  >
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{medal}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{entry.playerTag}</span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
                          padding: '2px 8px', borderRadius: 20,
                          border: `1px solid ${genreColor}55`, color: genreColor,
                          textTransform: 'uppercase',
                        }}>{entry.genre}</span>
                        {entry.archetype?.name && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{entry.archetype.name}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 3 }}>
                        {entry.shareableId}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 28, fontWeight: 900, color: rankColor, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                        {entry.scores?.overallScore ?? '—'}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, letterSpacing: 1, textTransform: 'uppercase' }}>
                        Overall
                      </div>
                    </div>
                  </motion.a>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              style={{ textAlign: 'center', marginTop: 24 }}
            >
              <Link to="/leaderboard" className="btn btn--ghost" style={{ fontSize: 13 }}>
                View Full Leaderboard →
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── FAQ ─────────────────────────────────────────────────────── */}
      <FAQ />

      {/* ── Social Proof ──────────────────────────────────────────────── */}
      <section className="home-section" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="section-eyebrow">WHAT PLAYERS ARE SAYING</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginTop: 32 }}>
              {[
                { quote: "I thought my aim was the problem. Turns out my decision quality was dragging me down the whole time. Changed how I approach ranked.", tag: "Diamond II — VALORANT", archetype: "Ghost Operator" },
                { quote: "Got my whole 5-stack to take it before our tournament. Our IGL started calling differently based on everyone's composure scores. We went 7-0.", tag: "Immortal — CS2", archetype: "Tactical Mastermind" },
                { quote: "Working memory: 38th percentile. That explains why I lose track of cooldowns every fight. At least now I know what to work on.", tag: "Plat III — Apex", archetype: "Chaos Pilot" },
              ].map((t, i) => (
                <div key={i} style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 12, padding: '24px 20px',
                  display: 'flex', flexDirection: 'column', gap: 16,
                }}>
                  <p style={{ margin: 0, fontSize: 14, color: '#ccc', lineHeight: 1.7, fontStyle: 'italic' }}>
                    "{t.quote}"
                  </p>
                  <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#666' }}>{t.tag}</span>
                    <span style={{ fontSize: 11, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>{t.archetype}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Bottom CTA ───────────────────────────────────────────────── */}
      <section className="home-section cta-section">
        <div className="cta-section__glow" />
        <div className="container">
          <motion.div
            className="cta-section__inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="cta-section__title">Ready to find out?</h2>
            <p className="cta-section__sub">
              Your neural profile is waiting. No account needed — just your player tag and 10 minutes.
            </p>
            <div className="cta-section__btns">
              <button
                className="btn btn--primary"
                style={{ fontSize: 16, padding: '14px 40px' }}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                Start Combine →
              </button>
              <Link to="/practice" className="btn btn--ghost" style={{ fontSize: 16, padding: '14px 40px' }}>
                Try Practice Mode
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="home-footer">
        <div className="container--wide">
          <div className="home-footer__top">
            <div className="home-footer__brand">
              <span className="home-footer__logo">NEURORANK</span>
              <p className="home-footer__tagline">Measure your mind. Prove your edge.</p>
            </div>
            <div className="home-footer__cols">
              <div className="home-footer__col">
                <span className="home-footer__col-heading">Platform</span>
                <Link to="/practice">Practice Mode</Link>
                <Link to="/leaderboard">Leaderboard</Link>
                <Link to="/scout">Scout Dashboard</Link>
                <Link to="/blog">Blog</Link>
                <Link to="/sensitivity">Sens Converter</Link>
                <Link to="/pricing">Pricing</Link>
              </div>
              <div className="home-footer__col">
                <span className="home-footer__col-heading">Community</span>
                <a href="https://discord.gg/W9Ftc6TC8J" target="_blank" rel="noopener noreferrer">Discord</a>
              </div>
              <div className="home-footer__col">
                <span className="home-footer__col-heading">Company</span>
                <Link to="/terms">Terms of Service</Link>
                <Link to="/privacy">Privacy Policy</Link>
                <a href="mailto:neurorank.gg@gmail.com">Contact</a>
              </div>
            </div>
          </div>
          <div className="home-footer__bottom">
            <span className="home-footer__copy">© 2026 NeuroRank. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </motion.div>
  );
}

// --- Shared NavBar for inner pages ---
function NavBar() {
  const { user, authLoading, logout } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const unreadCount = user?.notifications?.filter((n) => !n.read).length || 0;

  const closeMenu = () => setMenuOpen(false);

  // Show authenticated nav items if logged in, or optimistically during load
  // when a token exists — prevents the tab vanishing on full-page reloads.
  const showUserLinks = !!user || (authLoading && !!localStorage.getItem('nr_token'));

  const NAV_LINKS = [
    { to: '/practice',    label: 'Practice' },
    { to: '/leaderboard', label: 'Leaderboard' },
    { to: '/scout',       label: 'Scout' },
    { to: '/blog',        label: 'Blog' },
    { to: '/sensitivity', label: 'Sens Converter' },
    { to: '/pricing',     label: 'Pricing' },
    ...(showUserLinks ? [{ to: '/shortlist', label: '★ Shortlist' }, { to: '/my-profile', label: 'My Profile' }] : []),
  ];

  return (
    <>
      <nav style={{
        borderBottom: '1px solid var(--border)', padding: '0 20px', height: 60,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: 'rgba(6,9,15,0.85)', backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Link to="/" style={{ fontWeight: 900, fontSize: 14, color: 'var(--accent)', letterSpacing: '5px', textDecoration: 'none', textTransform: 'uppercase' }}>
          NEURORANK
        </Link>

        {/* Desktop nav */}
        <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
          <div className="app-nav__links" style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
            {NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', textDecoration: 'none', position: 'relative' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
              >
                {label}
                {label === 'My Profile' && unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -10,
                    background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700,
                    width: 15, height: 15, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </Link>
            ))}
          </div>
          {user ? (
            <div className="app-nav__auth" style={{ display: 'flex', alignItems: 'center', gap: 16, borderLeft: '1px solid var(--border)', paddingLeft: 20 }}>
              <Link to="/my-profile" style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 700, textDecoration: 'none', letterSpacing: '-0.01em' }}>{user.playerTag}</Link>
              <button onClick={logout} className="btn btn--ghost" style={{ padding: '6px 14px', fontSize: 11 }}>Sign Out</button>
            </div>
          ) : (
            <button onClick={() => setShowAuth(true)} className="btn btn--primary app-nav__signin" style={{ padding: '8px 18px' }}>Sign In</button>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="app-nav__hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
          style={{
            background: 'none', border: 'none',
            cursor: 'pointer', padding: 8, color: 'var(--text-primary)',
          }}
        >
          {menuOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="17" x2="21" y2="17"/></svg>
          )}
        </button>
      </nav>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="app-nav__dropdown" style={{
          position: 'fixed', top: 60, left: 0, right: 0, zIndex: 99,
          background: 'rgba(9,14,26,0.97)', backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid var(--border)',
          padding: '8px 0 16px',
        }}>
          {NAV_LINKS.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              onClick={closeMenu}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '13px 24px',
                fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'var(--text-secondary)', textDecoration: 'none',
                borderBottom: '1px solid var(--border)',
              }}
            >
              {label}
              {label === 'My Profile' && unreadCount > 0 && (
                <span style={{
                  background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700,
                  width: 15, height: 15, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </Link>
          ))}
          <div style={{ padding: '12px 24px 0' }}>
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 700 }}>{user.playerTag}</span>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="btn btn--ghost" style={{ padding: '6px 14px', fontSize: 11 }}>Sign Out</button>
              </div>
            ) : (
              <button onClick={() => { setShowAuth(true); setMenuOpen(false); }} className="btn btn--primary" style={{ width: '100%', padding: '11px' }}>Sign In</button>
            )}
          </div>
        </div>
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  );
}

// --- Inner page shell: nav + ambient background ---
function PageShell({ children }) {
  return (
    <>
      <NavBar />
      <div className="inner-page">
        <div className="inner-page__orb inner-page__orb--1" />
        <div className="inner-page__top-glow" />
        <div className="inner-page__grid" />
        <div className="inner-page__scan" />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
        >
          {children}
        </motion.div>
      </div>
    </>
  );
}

// --- Animated Routes (must be inside BrowserRouter) ---
// ── Cookie Banner ─────────────────────────────────────────────────────────
function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('nr_cookies_ok')) setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = () => {
    localStorage.setItem('nr_cookies_ok', '1');
    setVisible(false);
  };

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9000,
      background: 'var(--bg-card)', borderTop: '1px solid var(--border)',
      padding: '14px 24px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 16, flexWrap: 'wrap',
    }}>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
        We use cookies to improve your experience. By using NeuroRank you agree to our{' '}
        <Link to="/privacy" style={{ color: 'var(--accent)' }}>Privacy Policy</Link>.
      </p>
      <button className="btn btn--primary" style={{ padding: '7px 20px', fontSize: 13, flexShrink: 0 }} onClick={accept}>
        Got it
      </button>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
      <h1 style={{ fontSize: 64, fontWeight: 800, color: 'var(--accent)', margin: '0 0 8px' }}>404</h1>
      <p style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>Page not found</p>
      <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 32px' }}>The page you're looking for doesn't exist.</p>
      <Link to="/" className="btn btn--primary" style={{ padding: '10px 28px' }}>Go Home</Link>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  React.useEffect(() => {
    import('./services/track').then(({ pageview }) => pageview(location.pathname));
  }, [location.pathname]);
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Landing />} />
        <Route path="/test"                element={<PageShell><TestRunner /></PageShell>} />
        <Route path="/complete"            element={<CombineComplete />} />
        <Route path="/profile/:shareableId" element={<PageShell><NeuralProfile /></PageShell>} />
        <Route path="/scout"               element={<PageShell><ScoutDashboard /></PageShell>} />
        <Route path="/practice"            element={<PageShell><PracticeRunner /></PageShell>} />
        <Route path="/leaderboard"         element={<PageShell><Leaderboard /></PageShell>} />
        <Route path="/shortlist"           element={<PageShell><Shortlist /></PageShell>} />
        <Route path="/compare"             element={<PageShell><CompareView /></PageShell>} />
        <Route path="/auth/callback"       element={<AuthCallback />} />
        <Route path="/terms"               element={<PageShell><TermsOfService /></PageShell>} />
        <Route path="/privacy"             element={<PageShell><PrivacyPolicy /></PageShell>} />
        <Route path="/my-profile"          element={<PageShell><MyProfile /></PageShell>} />
        <Route path="/pricing"             element={<PageShell><Pricing /></PageShell>} />
        <Route path="/billing"             element={<PageShell><BillingPage /></PageShell>} />
        <Route path="/reset-password"      element={<ResetPassword />} />
        <Route path="/sensitivity"         element={<PageShell><SensitivityConverter /></PageShell>} />
        <Route path="/embed/leaderboard"   element={<EmbedLeaderboard />} />
        <Route path="/admin"               element={<AdminDashboard />} />
        <Route path="/challenge/:shareableId" element={<ChallengeView />} />
        <Route path="/blog"                element={<PageShell><Blog /></PageShell>} />
        <Route path="/blog/:slug"          element={<PageShell><BlogPost /></PageShell>} />
        <Route path="/for-coaches"         element={<ForCoaches />} />
        <Route path="/science"             element={<PageShell><Science /></PageShell>} />
        <Route path="*"                    element={<PageShell><NotFound /></PageShell>} />
      </Routes>
    </AnimatePresence>
  );
}

// --- App Router ---
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}
